'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface SupplierRecord {
  id: string;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

interface SupplierContextValue {
  suppliers: SupplierRecord[];
  supplierNames: string[];
  loading: boolean;
  addSupplier: (data: Omit<SupplierRecord, 'id'>) => Promise<void>;
  updateSupplier: (id: string, data: Omit<SupplierRecord, 'id'>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;
}

const SupplierContext = createContext<SupplierContextValue | null>(null);

export function SupplierProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();
  const supabase = createClient();
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const loadSuppliers = useCallback(async () => {
    if (!company?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, contact_name, email, phone, notes')
        .eq('company_id', company.id)
        .order('name');

      if (error) throw error;

      setSuppliers(
        (data ?? []).map((r) => ({
          id: r.id,
          name: r.name,
          contactName: r.contact_name ?? '',
          email: r.email ?? '',
          phone: r.phone ?? '',
          notes: r.notes ?? '',
        }))
      );
    } catch {
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const addSupplier = useCallback(async (data: Omit<SupplierRecord, 'id'>) => {
    if (!company?.id) return;
    const { data: inserted, error } = await supabase
      .from('suppliers')
      .insert({
        company_id: company.id,
        name: data.name,
        contact_name: data.contactName ?? null,
        email: data.email ?? null,
        phone: data.phone ?? null,
        notes: data.notes ?? null,
      })
      .select('id, name, contact_name, email, phone, notes')
      .single();

    if (!error && inserted) {
      setSuppliers((prev) => [
        ...prev,
        {
          id: inserted.id,
          name: inserted.name,
          contactName: inserted.contact_name ?? '',
          email: inserted.email ?? '',
          phone: inserted.phone ?? '',
          notes: inserted.notes ?? '',
        },
      ]);
    }
  }, [company?.id, supabase]);

  const updateSupplier = useCallback(async (id: string, data: Omit<SupplierRecord, 'id'>) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('suppliers')
      .update({
        name: data.name,
        contact_name: data.contactName ?? null,
        email: data.email ?? null,
        phone: data.phone ?? null,
        notes: data.notes ?? null,
      })
      .eq('id', id)
      .eq('company_id', company.id);

    if (!error) {
      setSuppliers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...data } : s))
      );
    }
  }, [company?.id, supabase]);

  const deleteSupplier = useCallback(async (id: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', id)
      .eq('company_id', company.id);

    if (!error) setSuppliers((prev) => prev.filter((s) => s.id !== id));
  }, [company?.id, supabase]);

  const supplierNames = suppliers.map((s) => s.name).sort((a, b) => a.localeCompare(b));

  return (
    <SupplierContext.Provider value={{ suppliers, supplierNames, loading, addSupplier, updateSupplier, deleteSupplier }}>
      {children}
    </SupplierContext.Provider>
  );
}

export function useSuppliers(): SupplierContextValue {
  const ctx = useContext(SupplierContext);
  if (!ctx) throw new Error('useSuppliers must be used within SupplierProvider');
  return ctx;
}
